import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Users, DollarSign, TrendingUp, Clock,
  Building2, CreditCard, AlertCircle, CheckCircle,
  Activity, PieChart, ChevronRight, RefreshCw,
  FileText, Server, ExternalLink
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';
import Loading from '../../components/Loading';

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get API base URL from environment (remove /api suffix for Hangfire)
  const getHangfireUrl = () => {
    const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || 'https://localhost:7086';
    const baseUrl = apiUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/hangfire`;
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getDashboard();
      setData(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <Loading />;

  if (error) {
    return (
      <div className="p-4 sm:p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-600">SuddyFlow Platform Overview</p>
        </div>
        <button
          onClick={loadDashboard}
          className="btn btn-secondary flex items-center justify-center gap-2 w-full sm:w-auto"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      {/* Revenue Cards - 2x2 on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="card p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs text-green-600 font-medium">MRR</p>
              <p className="text-lg sm:text-2xl font-bold text-green-900 truncate">{formatCurrency(data.mrr)}</p>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Monthly Recurring</p>
            </div>
            <DollarSign className="text-green-500 hidden sm:block flex-shrink-0" size={28} />
          </div>
        </div>

        <div className="card p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs text-blue-600 font-medium">ARR</p>
              <p className="text-lg sm:text-2xl font-bold text-blue-900 truncate">{formatCurrency(data.arr)}</p>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Annual Projected</p>
            </div>
            <TrendingUp className="text-blue-500 hidden sm:block flex-shrink-0" size={28} />
          </div>
        </div>

        <div className="card p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs text-purple-600 font-medium">Active Subs</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-900">{data.activeSubscriptions}</p>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Paying customers</p>
            </div>
            <CreditCard className="text-purple-500 hidden sm:block flex-shrink-0" size={28} />
          </div>
        </div>

        <div className="card p-3 sm:p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs text-amber-600 font-medium">Conversion</p>
              <p className="text-lg sm:text-2xl font-bold text-amber-900">{data.trialToPayConversionRate.toFixed(0)}%</p>
              <p className="text-xs text-gray-500 mt-1 hidden sm:block">Trial to paid</p>
            </div>
            <Activity className="text-amber-500 hidden sm:block flex-shrink-0" size={28} />
          </div>
        </div>
      </div>

      {/* Subscriber Status - Stack on mobile */}
      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        {/* Status Breakdown */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Subscriber Status</h3>
            <Link to="/admin/businesses" className="text-xs sm:text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-gray-600">Active</span>
              </div>
              <span className="font-semibold text-gray-900">{data.activeSubscriptions}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-600">Trial</span>
              </div>
              <span className="font-semibold text-gray-900">{data.trialAccounts}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="text-sm text-gray-600">Expired</span>
              </div>
              <span className="font-semibold text-gray-900">{data.expiredAccounts}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span className="text-sm text-gray-600">Cancelled</span>
              </div>
              <span className="font-semibold text-gray-900">{data.cancelledAccounts}</span>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Total Businesses</span>
                <span className="font-bold text-gray-900">{data.totalBusinesses}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Plan Breakdown */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Plan Distribution</h3>
            <PieChart size={18} className="text-gray-400" />
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span className="text-sm text-gray-600">Solo ($19)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{data.soloCount}</span>
                <span className="text-xs text-gray-400 hidden sm:inline">{formatCurrency(data.soloCount * 19)}/mo</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-sm text-gray-600">Pro ($49)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{data.proCount}</span>
                <span className="text-xs text-gray-400 hidden sm:inline">{formatCurrency(data.proCount * 49)}/mo</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className="text-sm text-gray-600">Enterprise ($129)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{data.enterpriseCount}</span>
                <span className="text-xs text-gray-400 hidden sm:inline">{formatCurrency(data.enterpriseCount * 129)}/mo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Trial Metrics */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">This Month</h3>
            <Clock size={18} className="text-gray-400" />
          </div>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">New Trials</span>
              <span className="font-semibold text-blue-600">{data.trialsStartedThisMonth}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Converted</span>
              <span className="font-semibold text-green-600">{data.trialsConvertedThisMonth}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Conversion Rate</span>
              <span className="font-semibold text-gray-900">{data.trialToPayConversionRate.toFixed(1)}%</span>
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Churn Rate</span>
                <span className={`font-semibold ${data.churnRatePercent > 5 ? 'text-red-600' : 'text-green-600'}`}>
                  {data.churnRatePercent.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Recent Activity</h3>
          <Activity size={18} className="text-gray-400" />
        </div>
        {data.recentActivity.length === 0 ? (
          <p className="text-gray-500 text-center py-8 text-sm">No recent activity</p>
        ) : (
          <div className="space-y-3">
            {data.recentActivity.map((activity, index) => (
              <div key={index} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activity.type === 'NewTrial' ? 'bg-blue-100' :
                  activity.type === 'Converted' ? 'bg-green-100' :
                  activity.type === 'Cancelled' ? 'bg-red-100' :
                  'bg-gray-100'
                }`}>
                  {activity.type === 'NewTrial' && <Clock size={14} className="text-blue-600" />}
                  {activity.type === 'Converted' && <CheckCircle size={14} className="text-green-600" />}
                  {activity.type === 'Cancelled' && <AlertCircle size={14} className="text-red-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{activity.businessName}</p>
                  <p className="text-xs text-gray-500 truncate">{activity.details}</p>
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                  {formatDate(activity.timestamp)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links - Scrollable on mobile */}
      <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-3">Quick Links</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        <Link
          to="/admin/businesses"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Building2 className="text-gray-400 flex-shrink-0" size={18} />
            <span className="font-medium text-gray-900 text-sm truncate">Businesses</span>
          </div>
          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
        </Link>
        
        <Link
          to="/admin/financials"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <DollarSign className="text-gray-400 flex-shrink-0" size={18} />
            <span className="font-medium text-gray-900 text-sm truncate">Financials</span>
          </div>
          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
        </Link>
        
        <Link
          to="/admin/expenses"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <CreditCard className="text-gray-400 flex-shrink-0" size={18} />
            <span className="font-medium text-gray-900 text-sm truncate">Expenses</span>
          </div>
          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
        </Link>

        <Link
          to="/admin/documentation"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <FileText className="text-gray-400 flex-shrink-0" size={18} />
            <span className="font-medium text-gray-900 text-sm truncate">Docs</span>
          </div>
          <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
        </Link>

        <a
          href={getHangfireUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Server className="text-orange-500 flex-shrink-0" size={18} />
            <span className="font-medium text-orange-900 text-sm truncate">Hangfire</span>
          </div>
          <ExternalLink size={14} className="text-orange-400 flex-shrink-0" />
        </a>

        <Link
          to="/admin/financials?view=schedule-c"
          className="card p-3 sm:p-4 hover:shadow-md transition-shadow flex items-center justify-between bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200"
        >
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <TrendingUp className="text-purple-500 flex-shrink-0" size={18} />
            <span className="font-medium text-purple-900 text-sm truncate">Schedule C</span>
          </div>
          <ChevronRight size={16} className="text-purple-400 flex-shrink-0" />
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;